import { screeningsApiClient, coreApi } from 'src/api/config';
import { useQuery } from 'react-query';
import Notifier from 'common/helpers/Notifier';
import { get } from 'lodash';
import {
  fetchUsersInfo,
  fetchScreeningsTemplates,
  fetchOrganizationsInfo,
} from 'src/actions/Screening/Group';

const LIMIT = 25; // maximum allowed by screening-ingestion

const getOffset = (pageNumber) => (pageNumber - 1) * LIMIT;
const getNextPage = (total, currentPage, size) => (
  total > currentPage * size ? currentPage + 1 : undefined
);
const getPrevPage = (currentPage) => (
  currentPage > 1 ? currentPage - 1 : undefined
);

const defaultOptions = {
  filters: [],
  page: 1,
  // type: 'screening',
  offset: getOffset(1),
  limit: LIMIT,
  target: '',
  source: 'self_screen,external',
};

async function verifySelfScreenClientConsent(screen, client, consentedClients) {
  const consented = consentedClients;
  if (consented.indexOf(client.id) < 0) {
    const response = await coreApi.createRecord('consent', {
      state: 'accepted',
      consent_method: 'written',
      submission_method: 'file_upload',
      person: client.id,
    });
    const id = get(response, 'data.data.id');
    console.log(id);
    // in case there are more than one screen for unconsented client
    consented.push(client.id);
    // await coreApi.uploadFile('consent', id, documentsFileList[0]);
    // await createDocumentConsent({contactId: client.id, documentsFileList: [], consentMethod: 'written'});
  }
  return consented;
}

function loadFilters(templates) {
  const templatesFilter = Object
    .values(templates.reduce((acc, t) => {
      const version = parseInt(t.version, 10);
      if (!acc[t.title] || version > parseInt(acc[t.title].version, 10)) acc[t.title] = t;
      return acc;
    }, {}))
    .map((t) => ({ label: t.title, value: t.type }));
  const typeFilter = {
    key: 'type',
    name: 'Screening Type',
    pluralName: 'Screening Types',
    options: templatesFilter,
  };
  const sourceFilter = {
    key: 'source',
    name: 'Screening Source',
    pluralName: 'Screening Sources',
    options: [
      { value: 'external', label: 'External' },
      { value: 'self_screen', label: 'Self Screen' },
    ],
  };
  const assignmentFilter = {
    key: 'is_assigned',
    name: 'Assignment Status',
    pluralName: 'Assigments Status',
    options: [
      { value: 'true', label: 'Assigned' },
      { value: 'false', label: 'Unassigned' },
    ],
  };
  return [assignmentFilter, sourceFilter, typeFilter];
}

const getFilters = (filters, params) => (
  filters.reduce((newParams, filter) => {
      const selectedValues = filter.options
        .filter((option) => option.initial)
        .map((option) => option.value);

      if (selectedValues.length > 0) {
        if (filter.key === 'is_assigned' && selectedValues.length > 1) {
          return { ...newParams, [filter.key]: null };
        }
        return { ...newParams, [filter.key]: selectedValues.join(',') };
      }
      return newParams;
    }, { ...params }));

async function fetchUserNames(screenings) {
  if (screenings) {
    const clientIds = [
      ...new Set(screenings.map((screen) => screen.subject.id).filter(Boolean)),
    ];

    return fetchUsersInfo(clientIds, []);
  }
  return [];
}

async function fetchOrganizations(screenings) {
  if (screenings) {
    const organizationIds = [
      ...new Set(screenings.map((screen) => screen.organization_id).filter(Boolean)),
    ];

    return fetchOrganizationsInfo(organizationIds);
  }
  return [];
}

export const SCREENINGS_KEYS = {
  EXTERNAL_SCREENINGS: 'external-screenings',
};

export const useExternalScreenings = (options = defaultOptions, providerId) => useQuery(
    [
      SCREENINGS_KEYS.EXTERNAL_SCREENINGS,
      options.page,
      options.filters,
      options.sortBy,
      options.sortDirection,
      providerId,
    ],
    async ({ signal }) => {
      try {
        let queryParams = {
          type: 'screening',
          status: 'complete',
          limit: LIMIT,
          offset: getOffset(options.page),
          sort_by: options.sortBy,
          sort_direction: options.sortDirection,
          client_relationship: providerId,
        };

        let filterOptions = options.filters;
        queryParams = getFilters(filterOptions, queryParams);

        if (!queryParams.source) queryParams.source = defaultOptions.source;

        let newTemplates = options.templates;
        // fetch type filters if empty
        if (filterOptions.length === 0) {
          newTemplates = await fetchScreeningsTemplates();
          filterOptions = loadFilters(newTemplates);
        }

        const response = await screeningsApiClient.get('/screenings', {
          params: queryParams,
          signal,
        });

        const screensList = get(response, 'data.screens', []);

        const pageData = {
          per: response.data.limit,
          next_page: getNextPage(
            response.data.total,
            options.page,
            response.data.limit,
          ),
          prev_page: getPrevPage(options.page),
          total_count: response.data.total,
          current_page: options.page,
        };

        const { clients } = await fetchUserNames(screensList);
        const { organizations } = await fetchOrganizations(screensList);

        let consentedClients = [];

        const screenings = screensList.map((screen) => {
          const client = clients.find((user) => user.id === screen.subject.id);

          if (screen.source === 'self_screen' && client?.consent === null) {
            consentedClients = verifySelfScreenClientConsent(
              screen,
              client,
              consentedClients,
            );
          }

          let submitter_name = 'Client';
          if (screen.source === 'external') {
            submitter_name = screen.organization_name && !screen.organization_id ?
              `${screen.organization_name} (External)` :
              'External';
          }

          const organization_name = organizations.find((o) => o.id === screen.organization_id)?.name || 'External';

          const type = newTemplates.find(
            (template) => template.id === screen.template_id,
          )?.title;

          return {
            ...screen,
            client_name: client?.full_name,
            submitter_name,
            organization_name,
            permissions: { can_view: { authorization: true } },
            assigned: screen.assigned_to_id ? 'Assigned' : 'Unassigned',
            type,
          };
        });

        const data = {
            screenings,
            paging: pageData,
            filters: filterOptions,
            templates: newTemplates,
        };

        return data;
      } catch (error) {
        if (error.name !== 'CanceledError') {
          Notifier.handleErrors(error);
        }
        return { screenings: [], paging: { total_count: 0 } };
      }
    },
    {
      // staleTime: 30 * 1000,
      // refetchInterval: 30 * 1000,
      retry: false,
      keepPreviousData: true,
    },
  );

export default useExternalScreenings;
